"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import { string, oneOfType, element, oneOf, func, bool, arrayOf, shape } from "prop-types";
import React, { useState, useRef } from "react";
import cx from "classnames";
import SSRIcon from "@ingka/ssr-icon";
import Button from "@ingka/button";
import HyperLink from "@ingka/hyperlink";
import closeIcon from "@ingka/ssr-icon/paths/cross-small";
import incorrectIcon from "@ingka/ssr-icon/paths/incorrect";
import warningIcon from "@ingka/ssr-icon/paths/warning-triangle";
import checkMarkIcon from "@ingka/ssr-icon/paths/checkmark-circle";
import informationIcon from "@ingka/ssr-icon/paths/information-circle";
const propTypes = {
  /** Function returning react element with paths for SSRIcon from `@ingka/ssr-icon/paths/[icon].js` */
  ssrIcon: func,
  /** Text read by screenreaders for the close button */
  labelCloseButton: string,
  /** Inline Message title. */
  title: oneOfType([string, element]),
  /** Text that is displayed */
  body: oneOfType([string, element]),
  /** URL for the if there is a link in the text */
  link: string,
  /** Link opens in new window */
  newWindow: bool,
  /** Text for the link */
  linkLabel: oneOfType([string, element]),
  /** Message variant one of ['informative', 'positive', 'cautionary', 'negative'] */
  variant: oneOf(["informative", "positive", "cautionary", "negative"]),
  /** Toggles subtle style variant */
  subtle: bool,
  /** Toggles message close button */
  dismissable: bool,
  /** Callback function that is called on dismiss button action. */
  onDismissClick: func,
  /** Prefix all classes in every element with this string */
  prefix: string,
  /** Appends additional classes to InlineMessage element. */
  className: string,
  /** Set render to `display:none` after closing to restore collapsed margins. Be aware that the collapse happens in an instant after the closing animation has finished causing a shift in the layout which may result in UX issues. */
  collapseWhenClosed: bool,
  actions: arrayOf(shape({
    text: oneOfType([string, element]),
    onClick: func,
    href: string,
    ssrIcon: func,
    iconPosition: oneOf(["leading", "trailing"]),
    loading: bool
  })),
  headingLevel: oneOf(["1", "2", "3", "4", "5", "6"])
};
const icons = {
  informative: informationIcon,
  positive: checkMarkIcon,
  cautionary: warningIcon,
  negative: incorrectIcon
};
const InlineMessage = (props) => {
  const {
    className,
    headingLevel = "6",
    actions,
    prefix = "",
    link,
    newWindow,
    linkLabel,
    body,
    ssrIcon,
    title,
    labelCloseButton,
    subtle,
    variant = "informative",
    dismissable,
    onDismissClick,
    collapseWhenClosed,
    ...other
  } = props;
  const wrapperRef = useRef(null);
  const isInformative = variant === "informative";
  const hideIcon = isInformative && !ssrIcon;
  const [isMessageClosed, setMessageClose] = useState(false);
  const [wrapperHeight, setWrapperHeight] = useState(null);
  const [collapsed, setCollapsed] = useState(false);
  const closeClick = () => {
    requestAnimationFrame(function() {
      setWrapperHeight(wrapperRef.current.scrollHeight);
      requestAnimationFrame(function() {
        setWrapperHeight(0);
      });
    });
    setMessageClose(true);
  };
  const handleTransitionEnd = (e) => {
    if (e.propertyName !== "height")
      return;
    setCollapsed(true);
    if (onDismissClick)
      onDismissClick(e);
  };
  const baseClass = `${prefix}inline-message`;
  const classes = cx(
    baseClass,
    `${baseClass}--${variant}`,
    isMessageClosed && `${baseClass}--closed`,
    hideIcon && `${baseClass}--icon-hidden`,
    subtle && `${baseClass}--subtle`,
    collapseWhenClosed && collapsed && `${baseClass}--collapsed`,
    dismissable && `${baseClass}--dismissable`,
    className
  );
  const HeadingTag = `h${headingLevel}`;
  return /* @__PURE__ */ jsxs(
    "div",
    {
      "aria-hidden": isMessageClosed ? "true" : "false",
      className: classes,
      ref: wrapperRef,
      style: { height: wrapperHeight },
      onTransitionEnd: handleTransitionEnd,
      ...other,
      children: [
        !hideIcon && /* @__PURE__ */ jsx(SSRIcon, { className: `${baseClass}__icon`, paths: ssrIcon ? ssrIcon : icons[variant], prefix }),
        /* @__PURE__ */ jsxs("div", { className: `${baseClass}__content`, children: [
          title && !subtle && /* @__PURE__ */ jsx(HeadingTag, { className: `${baseClass}__title`, children: title }),
          /* @__PURE__ */ jsxs("p", { className: `${baseClass}__body`, children: [
            body,
            " ",
            linkLabel && /* @__PURE__ */ jsx(HyperLink, { prefix, url: link, text: linkLabel, newWindow })
          ] }),
          !subtle && actions && /* @__PURE__ */ jsx("div", { className: `${baseClass}__actions`, children: actions.map((action, i) => {
            return /* @__PURE__ */ createElement(Button, { ...action, type: "tertiary", small: true, key: i, prefix });
          }) })
        ] }),
        dismissable && /* @__PURE__ */ jsx(
          Button,
          {
            prefix,
            type: "tertiary",
            small: true,
            xsmallIconOnly: true,
            iconOnly: true,
            ssrIcon: closeIcon,
            onClick: closeClick,
            className: `${baseClass}__close-button`,
            text: labelCloseButton
          }
        )
      ]
    }
  );
};
InlineMessage.propTypes = propTypes;
var InlineMessage_default = InlineMessage;
export {
  InlineMessage_default as default
};
